import { Line, ReelWinPosition } from '@cgs/data-access';
import { BuildAction, Logger } from '@cgs/shared';
import {
  Action,
  Container,
  EmptyAction,
  IntervalAction,
  ParallelAction,
  SequenceAction,
} from '@cgs/wge';
import { ILogoAnimationProvider } from '../../i_logo_animation_provider';
import { SpinConfig } from '../../../../reels_engine/game_config/game_config';
import { T_ILogoAnimationProvider } from '../../../../type_definitions';
import { AllWinLinesAction } from './all_win_lines_action';
import { IWinLinesAction } from './i_win_lines_action';
import { WinlineLineDrawer } from './winline_line_drawer';

export interface ExpandedShortWinlinesActionParams {
  container: Container;
  lineDrawer: WinlineLineDrawer | null;
  spinConfig: SpinConfig;
  winLines: Line[];
  winPositions: ReelWinPosition[];
}

export class ExpandedShortWinlinesAction extends BuildAction implements IWinLinesAction {
  protected readonly params: ExpandedShortWinlinesActionParams;

  constructor(params: ExpandedShortWinlinesActionParams) {
    super();

    this.params = params;
    Logger.Debug('load ' + this.constructor.name);
  }

  //-------- PUBLIC -------
  public get winLines(): Line[] {
    return this.params.winLines;
  }

  public buildAction(): Action {
    if (this.winLines.length === 0 && this.winPositions.length === 0) {
      return new EmptyAction().withDuration(this.spinConfig.noWinDelay);
    }

    const delayAction = new EmptyAction().withDuration(0.2);

    return new SequenceAction([
      new ParallelAction([
        new AllWinLinesAction(this.container, this.winLines),
        this.getLogoAnimationProviderAction(),
        this.getLinesAnimation(this.winLines),
      ]),
      this.getHideLinesAnimation(),
      delayAction,
    ]);
  }

  //------ PROTECTED ------
  protected get container(): Container {
    return this.params.container;
  }

  protected get winPositions(): ReelWinPosition[] {
    return this.params.winPositions;
  }

  protected get spinConfig(): SpinConfig {
    return this.params.spinConfig;
  }

  protected get lineDrawer(): WinlineLineDrawer | null {
    return this.params.lineDrawer;
  }

  protected getLogoAnimationProviderAction(): IntervalAction {
    const logoAnimationProvider = this.container.resolve<ILogoAnimationProvider>(
      T_ILogoAnimationProvider,
      { optional: true }
    );

    return logoAnimationProvider
      ? logoAnimationProvider.getShortWinLineAction()
      : new EmptyAction();
  }

  protected getLinesAnimation(winLines: Line[]): IntervalAction {
    return this.lineDrawer
      ? this.lineDrawer.drawWinlinesAction(winLines.map((x) => x.lineIndex))
      : new EmptyAction();
  }

  protected getLineAnimation(winLine: Line): IntervalAction {
    return this.lineDrawer
      ? this.lineDrawer.drawWinlineAction(winLine.lineIndex)
      : new EmptyAction();
  }

  protected getHideLinesAnimation(): IntervalAction {
    return this.lineDrawer ? this.lineDrawer.hideWinlinesAction() : new EmptyAction();
  }

  protected getHideLineAnimation(winLine: Line): IntervalAction {
    return this.lineDrawer
      ? this.lineDrawer.hideWinlineAction(winLine.lineIndex)
      : new EmptyAction();
  }
}
